/**
 * Equipment presets and the volume waterfall, kept out of the components.
 *
 * Two jobs:
 *
 * 1. **The catalog → document mapping.** The catalog mirrors core's
 *    `EquipmentProfile` field for field but cannot import it (PLAN §6: the
 *    catalog states facts, core owns the maths), so somebody has to join them.
 *    That somebody is here, spelled out field by field — which is what makes a
 *    drifting field a compile error rather than a silently missing loss.
 * 2. **The waterfall preview.** The equipment page shows what each loss field
 *    *does* by running core's real volume planner against a nominal batch as
 *    the brewer types. Same function the recipe editor uses; no second model.
 */

import type { EquipmentPreset } from '@beerwright/catalog';
import type { EquipmentProfileDoc, UnitPreferences } from '@beerwright/commands';
import { toEquipmentProfile } from '@beerwright/commands';
import type { VolumeWaterfall } from '@beerwright/core';
import { gallonsToLitres, planVolumes } from '@beerwright/core';
import { formatQuantity, unitLabel } from '@/lib/units';

/**
 * A catalog preset as an equipment profile document.
 *
 * Every field is named explicitly rather than spread: `EquipmentPreset` carries
 * catalog-only fields (`maker`, `description`, `tags`, `provenance`,
 * `nominalBatchVolumeL`) that have no place in a workspace document, and a
 * spread would smuggle them past `equipmentProfileDocSchema` — which is a
 * `strictObject` and would reject the command at dispatch time with a message
 * about unknown keys. Listing them also means adding a field to core's profile
 * without adding it here fails `pnpm typecheck`.
 *
 * The preset's `notes` are copied in with its provenance appended, because a
 * brewer looking at these numbers in six months should be able to tell they
 * came from a preset and are class-typical rather than measured.
 *
 * @param preset - The catalog entry.
 * @param options - Identity and timestamps; this app mints ids, the reducer
 *   does not (see the commands README).
 */
export function presetToEquipmentDoc(
  preset: EquipmentPreset,
  options: { readonly id: string; readonly name?: string; readonly timestamp: number },
): EquipmentProfileDoc {
  return {
    id: options.id,
    name: options.name ?? preset.name,
    builtin: false,
    notes: `${preset.notes}\n\nCreated from the ${preset.name} preset (${preset.maker}). Figures are ${preset.provenance}: recalibrate boil-off and mash efficiency from your first few batches.`,
    spargeType: preset.spargeType,
    mashTunDeadspaceL: preset.mashTunDeadspaceL,
    mashTunLossL: preset.mashTunLossL,
    grainAbsorptionLPerKg: preset.grainAbsorptionLPerKg,
    mashThicknessLPerKg: preset.mashThicknessLPerKg,
    boilOffRateLPerHour: preset.boilOffRateLPerHour,
    boilShrinkagePercent: preset.boilShrinkagePercent,
    kettleDeadspaceL: preset.kettleDeadspaceL,
    hopAbsorptionMlPerG: preset.hopAbsorptionMlPerG,
    dryHopAbsorptionMlPerG: preset.dryHopAbsorptionMlPerG,
    chillerLossL: preset.chillerLossL,
    fermenterTrubLossL: preset.fermenterTrubLossL,
    topUpKettleL: preset.topUpKettleL,
    topUpFermenterL: preset.topUpFermenterL,
    mashEfficiencyPercent: preset.mashEfficiencyPercent,
    brewhouseEfficiencyPercent: preset.brewhouseEfficiencyPercent,
    mashTunThermalMassLEquivalent: preset.mashTunThermalMassLEquivalent,
    mashTunInitialTempC: preset.mashTunInitialTempC,
    mashStandingLossCPerHour: preset.mashStandingLossCPerHour,
    // The pot a brewer decocts into is their own — no system in the catalog
    // ships one — so these two take the engine's defaults and are edited on the
    // profile rather than chosen by the rig.
    decoctionBoilOffLPerHour: 1.5,
    decoctionPotVolumeL: null,
    kettleVolumeL: preset.kettleVolumeL,
    mashTunVolumeL: preset.mashTunVolumeL,
    // A fermenter is not part of any rig in the catalog — a Foundry is sold
    // without one and brewers pick their own — so the preset leaves it unset and
    // the brewer fills it in on the profile.
    fermenterVolumeL: null,
    // Reminders are chores the brewer's own kit asks for on brew day. A preset
    // describes a class of rig, not anyone's routine, so it starts with none.
    brewReminders: [],
    createdAt: options.timestamp,
    updatedAt: options.timestamp,
  };
}

/**
 * The nominal brew the equipment page's waterfall is drawn against.
 *
 * A profile on its own has no waterfall — losses are per-kilogram and per-hour,
 * so they need a grist and a boil to become litres. Rather than invent a recipe,
 * the page pins one plausible brew and says so on screen: **5 kg of grain, 60 g
 * of kettle hops, a 60 minute boil**, which is a 1.050-ish pale ale on a 5 gal
 * system. The batch volume comes from the profile's own scale so a 1 bbl nano
 * does not get drawn against a homebrew batch.
 *
 * Everything scales with the batch, so the *shape* of the waterfall — which is
 * what the page is actually teaching — is right at any size.
 */
export const NOMINAL_BREW = {
  grainKg: 5,
  kettleHopKg: 0.06,
  dryHopKg: 0,
  boilTimeMinutes: 60,
} as const;

/** The batch volume a profile's preview is drawn at, litres — 5 US gallons. */
export const DEFAULT_PREVIEW_BATCH_L = gallonsToLitres(5);

/**
 * The volume waterfall a profile produces for a nominal batch.
 *
 * Runs core's `planVolumes` — the same backwards pass ("from a target batch
 * volume to the water the brewer must measure out") the recipe editor uses, so
 * the preview and the editor cannot disagree.
 *
 * @param profile - The profile as currently edited, saved or not.
 * @param batchVolumeL - Target volume into the fermenter.
 * @param grainKg - Grist size; scaled with the batch by the caller.
 */
export function previewWaterfall(
  profile: EquipmentProfileDoc,
  batchVolumeL: number,
  grainKg: number = NOMINAL_BREW.grainKg,
): VolumeWaterfall {
  return planVolumes(batchVolumeL, 'fermenter', toEquipmentProfile(profile), {
    grainKg,
    kettleHopKg: NOMINAL_BREW.kettleHopKg,
    dryHopKg: NOMINAL_BREW.dryHopKg,
    boilTimeMinutes: NOMINAL_BREW.boilTimeMinutes,
  });
}

/**
 * A grist proportional to the batch, so a 124 L nano is not previewed against a
 * homebrew grain bill.
 *
 * 5 kg per 5 gal is the ratio {@link NOMINAL_BREW} encodes; keeping it fixed is
 * what makes the waterfall's proportions comparable between systems.
 */
export function nominalGrainKg(batchVolumeL: number): number {
  return (NOMINAL_BREW.grainKg / DEFAULT_PREVIEW_BATCH_L) * batchVolumeL;
}

/** One bar of the waterfall chart: a stage and the volume at it. */
export interface WaterfallStage {
  readonly key: string;
  readonly label: string;
  readonly volumeL: number;
  /** What was lost between the previous stage and this one, litres. */
  readonly lossL: number;
  /** One line naming the fields responsible, so the chart explains itself. */
  readonly detail: string;
}

/**
 * The waterfall as the five stages PLAN §5.7 names, with the loss between each.
 *
 * Strike and sparge are shown as one "water in" bar because they are the same
 * water measured into two vessels — splitting them would suggest a loss between
 * them that does not exist.
 */
export function waterfallStages(
  waterfall: VolumeWaterfall,
  units: UnitPreferences,
): readonly WaterfallStage[] {
  // One trailing unit per line: the figures on a line are the same measure, and
  // repeating "gal" three times reads like three different things.
  const fmt = (litres: number) => formatQuantity(litres, 'volume', units, { decimals: 1 });
  const unit = unitLabel('volume', units);
  // Deadspace is deliberately absent: it returns to the kettle, so it moves no
  // bar of this chart. Only `mashTunLossL` is wort the brewer does not get back.
  const mashLoss = waterfall.grainAbsorptionL + waterfall.mashTunLossL;
  const boilLoss = waterfall.boilOffL + waterfall.shrinkageL;
  const kettleLoss =
    waterfall.kettleDeadspaceL + waterfall.kettleHopAbsorptionL + waterfall.chillerLossL;
  const packageLoss = waterfall.fermenterTrubLossL + waterfall.dryHopAbsorptionL;

  return [
    {
      key: 'water',
      label: 'Water in',
      volumeL: waterfall.totalWaterL,
      lossL: 0,
      detail: `Strike ${fmt(waterfall.strikeWaterL)} + sparge ${fmt(waterfall.spargeWaterL)} ${unit}`,
    },
    {
      key: 'preBoil',
      label: 'Pre-boil',
      volumeL: waterfall.preBoilVolumeL,
      lossL: mashLoss,
      detail: `Grain absorption ${fmt(waterfall.grainAbsorptionL)} + mash tun loss ${fmt(waterfall.mashTunLossL)} ${unit}`,
    },
    {
      key: 'postBoil',
      label: 'Post-boil',
      volumeL: waterfall.postBoilVolumeL,
      lossL: boilLoss,
      detail: `Boil-off ${fmt(waterfall.boilOffL)} + 4 % shrinkage ${fmt(waterfall.shrinkageL)} ${unit}`,
    },
    {
      key: 'fermenter',
      label: 'Fermenter',
      volumeL: waterfall.volumeIntoFermenterL,
      lossL: kettleLoss,
      detail: `Kettle deadspace ${fmt(waterfall.kettleDeadspaceL)} + hop absorption ${fmt(waterfall.kettleHopAbsorptionL)} + chiller ${fmt(waterfall.chillerLossL)} ${unit}`,
    },
    {
      key: 'packaged',
      label: 'Packaged',
      volumeL: waterfall.packagedVolumeL,
      lossL: packageLoss,
      detail: `Trub and yeast ${fmt(waterfall.fermenterTrubLossL)} + dry hop absorption ${fmt(waterfall.dryHopAbsorptionL)} ${unit}`,
    },
  ];
}

/**
 * The editable fields of an equipment profile, grouped the way the form lays
 * them out.
 *
 * Grouped by *what the brewer is thinking about*, not by type: the volumes they
 * measure, the losses they lose, the rates they calibrate, and the two
 * efficiency numbers. `kind` picks the input widget and the unit conversion.
 */
export const EQUIPMENT_FIELD_GROUPS = [
  {
    title: 'Vessels',
    hint: "Capacities don't change any of your numbers. We use them to warn you before brew day about a boil that won't fit the kettle, a mash that won't fit the tun, or a batch with no headspace.",
    fields: [
      { key: 'kettleVolumeL', label: 'Kettle capacity', kind: 'volume', nullable: true },
      { key: 'mashTunVolumeL', label: 'Mash tun capacity', kind: 'volume', nullable: true },
      { key: 'fermenterVolumeL', label: 'Fermenter capacity', kind: 'volume', nullable: true },
      { key: 'decoctionPotVolumeL', label: 'Decoction pot', kind: 'volume', nullable: true },
    ],
  },
  {
    title: 'Losses',
    hint: "Wort you lose along the way. Each one changes a step of the chart on the right. Mash tun deadspace is here so we know about it, but it doesn't cost you any volume.",
    fields: [
      { key: 'mashTunDeadspaceL', label: 'Mash tun deadspace', kind: 'volume', nullable: false },
      { key: 'mashTunLossL', label: 'Mash tun loss', kind: 'volume', nullable: false },
      { key: 'kettleDeadspaceL', label: 'Kettle deadspace', kind: 'volume', nullable: false },
      { key: 'chillerLossL', label: 'Chiller & hose loss', kind: 'volume', nullable: false },
      { key: 'fermenterTrubLossL', label: 'Fermenter trub loss', kind: 'volume', nullable: false },
      { key: 'topUpKettleL', label: 'Kettle top-up', kind: 'volume', nullable: false },
      { key: 'topUpFermenterL', label: 'Fermenter top-up', kind: 'volume', nullable: false },
    ],
  },
  {
    title: 'Rates',
    hint: 'Per-kilogram and per-hour figures. These are the ones worth measuring on your own system.',
    fields: [
      {
        key: 'grainAbsorptionLPerKg',
        label: 'Grain absorption',
        kind: 'volumePerGrainMass',
        nullable: false,
      },
      {
        key: 'mashThicknessLPerKg',
        label: 'Mash thickness',
        kind: 'volumePerGrainMass',
        nullable: false,
      },
      { key: 'boilOffRateLPerHour', label: 'Boil-off', kind: 'volumeRate', nullable: false },
      {
        key: 'boilShrinkagePercent',
        label: 'Boil shrinkage (%)',
        kind: 'percent',
        nullable: false,
      },
      {
        key: 'hopAbsorptionMlPerG',
        label: 'Kettle hop absorption',
        kind: 'volumePerHopMass',
        nullable: false,
      },
      {
        key: 'dryHopAbsorptionMlPerG',
        label: 'Dry hop absorption',
        kind: 'volumePerHopMass',
        nullable: false,
      },
      {
        key: 'decoctionBoilOffLPerHour',
        label: 'Decoction pot boil-off',
        kind: 'volumeRate',
        nullable: false,
      },
    ],
  },
  {
    title: 'Efficiency & thermal',
    hint: 'Mash efficiency drives gravity; brewhouse efficiency is recorded from batch actuals and is informational here.',
    fields: [
      {
        key: 'mashEfficiencyPercent',
        label: 'Mash efficiency (%)',
        kind: 'percent',
        nullable: false,
      },
      {
        key: 'brewhouseEfficiencyPercent',
        label: 'Brewhouse efficiency (%)',
        kind: 'percent',
        nullable: false,
      },
      {
        key: 'mashTunThermalMassLEquivalent',
        label: 'Tun thermal mass (water equivalent)',
        kind: 'volume',
        nullable: false,
      },
      {
        key: 'mashTunInitialTempC',
        label: 'Tun temperature at dough-in',
        kind: 'temperature',
        nullable: false,
      },
      {
        key: 'mashStandingLossCPerHour',
        label: 'Mash standing loss',
        kind: 'temperaturePerHour',
        nullable: false,
      },
    ],
  },
] as const;

/** A numeric field of an equipment profile the form can edit. */
export type EquipmentFieldKey = (typeof EQUIPMENT_FIELD_GROUPS)[number]['fields'][number]['key'];

/**
 * What a field measures — the display unit, the parser and the label's suffix.
 *
 * The three ratio kinds are `lib/units`' {@link RatioKind}s: a form field is
 * the one place a wrong unit is not merely misread but *stored*, so they are
 * named here rather than left as `plain` numbers with the unit written into
 * the label by hand.
 */
export type EquipmentFieldKind = (typeof EQUIPMENT_FIELD_GROUPS)[number]['fields'][number]['kind'];

/**
 * A line under a field, for the ones a group heading cannot explain.
 *
 * The two mash-tun figures need one badly: they sound like synonyms, sit next
 * to each other, and do opposite things. A brewer copying numbers out of
 * Brewfather has both fields in front of them there too, so the wording
 * deliberately matches what those fields mean over there — entering a
 * Brewfather deadspace here used to inflate strike water by the volume under
 * the grain basket, which is the whole reason they are separate.
 *
 * The three decoction fields need one because they do nothing at all until a
 * mash schedule has a decoction step in it, and a field that is inert on most
 * recipes has to say so where the brewer meets it.
 */
export const EQUIPMENT_FIELD_HINTS: Partial<Record<EquipmentFieldKey, string>> = {
  mashTunDeadspaceL:
    "Below the outlet, returns to the kettle (all-in-ones). Doesn't change water amounts.",
  mashTunLossL: 'Wort genuinely left behind in the tun. Adds to the water you need.',
  mashStandingLossCPerHour:
    'How fast the mash cools with the lid on. We only use it for decoctions, so set it to 0 if your rig holds mash temperature for you.',
  decoctionBoilOffLPerHour:
    'Evaporation from the decoction pot, not the kettle. This water never reaches the boil, so it adds to the water you need.',
  fermenterVolumeL:
    "The size of the fermenter itself. We use it to warn you when there's not enough headspace for the krausen.",
  decoctionPotVolumeL: 'Only warns you when a pull would not fit.',
};

// ---------------------------------------------------------------------------
// The measured figures a batch writes back
// ---------------------------------------------------------------------------

/**
 * The three profile fields a batch's measurements can back-compute, and that
 * `equipmentFeedback` therefore offers back to the profile.
 */
export type MeasuredProfileKey =
  | 'mashEfficiencyPercent'
  | 'brewhouseEfficiencyPercent'
  | 'boilOffRateLPerHour';

/**
 * What adopting a measured figure actually changes, in one line.
 *
 * The three figures do not mean the same thing to the engine, and the copy on
 * both write-back surfaces used to say they did. Mash efficiency is what
 * `predictGravities` is handed, and the boil-off rate sets the volume waterfall
 * the gravity is computed over; between them they move every prediction on the
 * profile. **Brewhouse efficiency moves nothing.** Core's own type doc calls it
 * informational, `predictGravities` never receives it, and the engine derives
 * its own figure from the mash efficiency and the losses. It is stored because
 * brewers record it from batch actuals, and recording it is all it does.
 *
 * Written once and read from both the batch card and the equipment page,
 * because they are two views of one aggregate and the last time they each
 * carried their own sentence, they were each wrong in the same way.
 *
 * @param keys - The figures the surface is actually offering: the batch card's
 *   rows, or the equipment page's rows that have a median behind them.
 * @returns The sentences for those figures, or `''` when there are none — an
 *   empty string so the caller can decide whether an empty paragraph is worth
 *   rendering.
 */
export function whatAdoptingMoves(keys: readonly MeasuredProfileKey[]): string {
  const predicted: string[] = [];
  const figures: string[] = [];
  if (keys.includes('mashEfficiencyPercent')) {
    figures.push('mash efficiency');
    predicted.push('the gravity');
  }
  if (keys.includes('boilOffRateLPerHour')) {
    figures.push('boil-off rate');
    predicted.push('the volumes');
  }

  const sentences: string[] = [];
  if (figures.length > 0) {
    sentences.push(
      `Your ${figures.join(' and ')} move${figures.length === 1 ? 's' : ''} ${predicted.join(' and ')} we predict for every recipe on this profile.`,
    );
  }
  if (keys.includes('brewhouseEfficiencyPercent')) {
    sentences.push(
      "We record brewhouse efficiency so you can see how the whole day went, but we don't use it in any predictions.",
    );
  }
  return sentences.join(' ');
}
